import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import useReveal from "@/hooks/useReveal";
import ProductPreview from "@/components/ProductPreview";
import { ArrowRight } from "@phosphor-icons/react";

export default function Products() {
  const ref = useReveal();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/products").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const openQuote = (e, slug) => {
    e.preventDefault();
    e.stopPropagation();
    nav(`/products/${slug}?demo=1`);
  };

  return (
    <section id="products" ref={ref} data-testid="products" className="reveal section bg-[color:var(--paper-alt)] border-b border-[color:var(--line)] relative overflow-hidden">
      <div className="absolute -top-32 right-0 w-72 h-72 bg-[color:var(--brand)] opacity-10 blur-3xl pointer-events-none" aria-hidden />
      <div className="container-x relative">
        <div className="flex items-start gap-3 mb-3">
          <span className="mt-1 w-6 h-px bg-[color:var(--brand)]" />
          <div className="eyebrow">Products</div>
        </div>
        <div className="grid lg:grid-cols-12 gap-6 md:gap-8 mb-10 md:mb-14">
          <h2 className="lg:col-span-8 font-display font-black text-4xl md:text-6xl leading-[1.02] tracking-tight">
            Deploy in 30 days, not 30 weeks
          </h2>
          <p className="lg:col-span-4 text-neutral-700 text-base md:text-lg leading-relaxed lg:pt-4">
            Twelve vertical systems, already built and proven — subscribe monthly or license outright.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {items.map((p) => (
            <Link
              to={`/products/${p.slug}`}
              key={p.id}
              data-testid={`product-${p.slug}`}
              className="group bg-white border border-[color:var(--line)] hover:border-[color:var(--brand)] transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(30,136,240,0.4)] block"
            >
              <div className="aspect-[16/10] bg-neutral-100 border-b border-[color:var(--line)] overflow-hidden">
                <ProductPreview preview_key={p.preview_key} />
              </div>
              <div className="p-5 md:p-6">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--brand)]">{p.industry || (p.industries || [])[0]}</div>
                <h3 className="mt-2 font-display font-bold text-xl md:text-2xl leading-tight">{p.title}</h3>
                <p className="mt-2 md:mt-3 text-sm text-neutral-600 leading-relaxed line-clamp-2">{p.tagline}</p>
                <div className="mt-5 pt-4 border-t border-[color:var(--line)] flex items-center justify-between gap-3">
                  <span className="font-mono text-xs uppercase tracking-widest text-neutral-500">Monthly · yearly · outright</span>
                  <ArrowRight size={16} weight="bold" className="text-neutral-400 group-hover:text-[color:var(--brand)] group-hover:translate-x-1 transition-all" />
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => openQuote(e, p.slug)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openQuote(e, p.slug)}
                    data-testid={`quote-${p.slug}`}
                    className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest bg-[color:var(--brand)] text-white px-3 py-1.5 hover:bg-[color:var(--brand-dark)] transition-colors cursor-pointer"
                  >
                    Get a quote
                  </span>
                  <span className="font-mono text-[11px] text-[color:var(--brand)] border-b border-[color:var(--brand)] pb-0.5">
                    Book a 20-min demo
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
